import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, ChevronLeft, Search, Sparkles, RefreshCw, AlertTriangle, Circle,
    Archive, RotateCcw, Info, Mail, HardDrive, Wrench, X, Check, Send,
    Brush, Image as ImageIcon, CalendarDays,
} from "lucide-react";
import {
    adminListContentPackages,
    adminGetContentPackage,
    adminGenerateMissingAssets,
    adminRegenerateAllAssets,
    adminBulkGenerateMissingAssets,
    adminBulkRegenerateAllAssets,
    adminBulkRebrandImages,
    adminBulkUnarchiveLegacy,
    adminBulkGenerateMissingHeroes,
    adminRegenerateHeroImage,
    adminBulkQAAutoFix,
    adminLegacyArchiveStatus,
    adminLegacyArchiveRun,
    adminLegacyRestorePackage,
    adminSendPackageToPinterest,
} from "@/lib/api";
import { toast } from "sonner";
import GeneratePackageForm from "@/components/admin/content/GeneratePackageForm";
import ContentPackageEditor from "@/components/admin/content/ContentPackageEditor";
import OpportunityCards from "@/components/admin/content/OpportunityCards";
import ContentEngineSpendTile from "@/components/admin/content/ContentEngineSpendTile";
import ContentEngineHealthWidget from "@/components/admin/content/ContentEngineHealthWidget";
import ContentJobsTile from "@/components/admin/ContentJobsTile";
import ImageStatusTile from "@/components/admin/content/ImageStatusTile";
import UniversalKeyBalanceTile from "@/components/admin/content/UniversalKeyBalanceTile";
import AssetCompletionDot from "@/components/admin/content/AssetCompletionDot";
import PackageProgressLine from "@/components/admin/content/PackageProgressLine";

const STATUS_COLORS = {
    draft: "text-bl-muted",
    generated: "text-bl-primary",
    needs_review: "text-[var(--bl-amber)]",
    approved: "text-[var(--bl-green)]",
    scheduled: "text-bl-primary",
    published: "text-[var(--bl-green)]",
    archived: "text-bl-muted line-through",
};

export default function AdminContentPackages() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [refreshTick, setRefreshTick] = useState(0);
    const [bulkBusy, setBulkBusy] = useState(null); // "missing" | "all" | null
    const [rowBusy, setRowBusy] = useState({}); // { [package_id]: "missing"|"all" }
    const [confirmBulkRegen, setConfirmBulkRegen] = useState(false);
    const [confirmBulkRebrand, setConfirmBulkRebrand] = useState(false);
    const [bulkQAPreview, setBulkQAPreview] = useState(null);
    const [bulkQAModalOpen, setBulkQAModalOpen] = useState(false);
    const [bulkQABusy, setBulkQABusy] = useState(false);
    const [bulkQAApplying, setBulkQAApplying] = useState(false);
    const formRef = useRef(null);
    // V1 Lean Content Engine — clean-slate legacy archive controls.
    // viewMode: "active" (default, hides legacy) | "legacy" (legacy-only).
    const [viewMode, setViewMode] = useState("active");
    const [legacyStatus, setLegacyStatus] = useState(null);
    const [legacyBusy, setLegacyBusy] = useState(false);
    const [confirmLegacyRun, setConfirmLegacyRun] = useState(false);
    const [restoreBusy, setRestoreBusy] = useState({});

    // Live-poll while any row is generating so the dot + per-stage
    // progress flip in real time. Trigger on EVERY in-flight phase
    // (article gen, image pipeline, manual hero regen, asset gen) so
    // we don't go silent in the middle of a multi-stage run.
    const hasInProgress = useMemo(
        () => list.some(
            (p) =>
                p.asset_generation_status === "in_progress"
                || p.asset_generation_status === "pending"
                || p.status === "generating"
                || p.image_pipeline_status === "generating"
                || p.hero_regeneration_status === "in_progress"
                || p.asset_completion?.hero_regen_active,
        ),
        [list],
    );
    useEffect(() => {
        if (!hasInProgress) return;
        const id = setInterval(() => setRefreshTick((t) => t + 1), 5000);
        return () => clearInterval(id);
    }, [hasInProgress]);

    const missingTierCount = useMemo(
        () => list.filter(
            (p) => p.asset_completion && p.asset_completion.tier !== "green",
        ).length,
        [list],
    );

    // Total stale-platform debt across the entire library. A package
    // counts in `staleArticleCount` if any of its 7 social platforms is
    // stale; `stalePlatformTotal` is the raw platform sum (e.g. 5 stale
    // articles × ~3 platforms each = 15 platforms to refresh).
    const { staleArticleCount, stalePlatformTotal } = useMemo(() => {
        let articles = 0;
        let platforms = 0;
        for (const p of list) {
            const n = p?.freshness?.stale || 0;
            if (n > 0) {
                articles += 1;
                platforms += n;
            }
        }
        return { staleArticleCount: articles, stalePlatformTotal: platforms };
    }, [list]);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const params = statusFilter ? { status: statusFilter } : {};
                if (viewMode === "legacy") params.only_legacy = true;
                const r = await adminListContentPackages(params);
                if (alive) setList(r.items || []);
            } catch {
                toast.error("Could not load content packages.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [statusFilter, refreshTick, viewMode]);

    // Load legacy-archive status on mount and after migration runs.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminLegacyArchiveStatus();
                if (alive) setLegacyStatus(r);
            } catch {
                /* swallow — status panel just won't render */
            }
        })();
        return () => {
            alive = false;
        };
    }, [refreshTick]);

    const handleLegacyRun = async () => {
        setLegacyBusy(true);
        try {
            const r = await adminLegacyArchiveRun();
            toast.success(
                `Migration complete: ${r.packages_archived} package(s) archived, ${r.articles_reset} article(s) reset.`,
            );
            setRefreshTick((t) => t + 1);
            setConfirmLegacyRun(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Migration failed.");
        } finally {
            setLegacyBusy(false);
        }
    };

    const handleLegacyRestore = async (pid) => {
        setRestoreBusy((s) => ({ ...s, [pid]: true }));
        try {
            await adminLegacyRestorePackage(pid);
            toast.success("Restored to active workflow.");
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed.");
        } finally {
            setRestoreBusy((s) => {
                const next = { ...s };
                delete next[pid];
                return next;
            });
        }
    };

    const openPackage = async (package_id) => {
        try {
            const pkg = await adminGetContentPackage(package_id);
            if (!pkg) {
                toast.error("Package not found.");
                return;
            }
            setSelected(pkg);
            // Scroll to top so the editor header is immediately in view
            // (otherwise opening a row mid-list leaves the editor scrolled
            // deep into the article sections — hard to tell anything changed).
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "instant" });
            }, 30);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not load package.",
            );
        }
    };

    const handleRowGenerate = async (pid) => {
        setRowBusy((s) => ({ ...s, [pid]: "missing" }));
        try {
            await adminGenerateMissingAssets(pid);
            toast.success("Queued — generating missing assets in background.");
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to queue.");
        } finally {
            setRowBusy((s) => {
                const next = { ...s };
                delete next[pid];
                return next;
            });
        }
    };

    const handleRowRegenerate = async (pid) => {
        if (!window.confirm(
            "Regenerate ALL assets for this package? This wipes hero image + every social asset and re-runs from scratch (~$0.50–1.00 in LLM credit, ~2-4 min).",
        )) return;
        setRowBusy((s) => ({ ...s, [pid]: "all" }));
        try {
            await adminRegenerateAllAssets(pid);
            toast.success("Queued — full regen running in background.");
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to queue.");
        } finally {
            setRowBusy((s) => {
                const next = { ...s };
                delete next[pid];
                return next;
            });
        }
    };

    const handleRowRegenerateHero = async (pid) => {
        setRowBusy((s) => ({ ...s, [pid]: "hero" }));
        try {
            await adminRegenerateHeroImage(pid);
            toast.success(
                "Hero queued — Nano Banana is rendering. Should land in ~30 s.",
            );
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to queue hero regen.",
            );
        } finally {
            setRowBusy((s) => {
                const next = { ...s };
                delete next[pid];
                return next;
            });
        }
    };

    // Schedule all 8 Pinterest pins for a package evenly across 30
    // days via Metricool. Each pin gets ~4 days of breathing room
    // (`spread_days=30` ÷ 7 gaps), starts firing tomorrow at 14:00 UTC
    // (≈ 9 AM US Central), and auto-routes to the right board via the
    // `ANGLE_TO_BOARD` table. Confirm before firing — this hits the
    // live Metricool API, not a dry-run.
    const handleRowScheduleMonth = async (pid, title) => {
        const ok = window.confirm(
            `Schedule all 8 Pinterest pins for "${title || pid}" evenly over 30 days?\n\n`
            + "First pin fires tomorrow at 14:00 UTC (≈ 9 AM Central). "
            + "Each subsequent pin ~4 days apart. Pins auto-route to the matching board.\n\n"
            + "This calls the LIVE Metricool API — pins will actually publish."
        );
        if (!ok) return;
        setRowBusy((s) => ({ ...s, [pid]: "schedule" }));
        try {
            const r = await adminSendPackageToPinterest(pid, {
                spread_days: 30,
                start_in_days: 1,
                daily_hour_utc: 14,
            });
            const scheduled = r?.scheduled ?? 0;
            const total = r?.total ?? 0;
            if (scheduled === total) {
                toast.success(
                    `Scheduled ${scheduled}/${total} pins over 30 days · first pin tomorrow at 14:00 UTC.`,
                );
            } else {
                const firstErr = (r?.results || []).find((x) => !x.scheduled)?.error;
                toast.warning(
                    `Scheduled ${scheduled}/${total} — see console for the rest.${firstErr ? ` First error: ${firstErr}` : ""}`,
                );
                // eslint-disable-next-line no-console
                console.warn("[metricool] partial schedule", r);
            }
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to schedule package to Pinterest.",
            );
        } finally {
            setRowBusy((s) => {
                const next = { ...s };
                delete next[pid];
                return next;
            });
        }
    };

    const handleBulkGenerateMissingHeroes = async () => {
        setBulkBusy("missing-heroes");
        try {
            const r = await adminBulkGenerateMissingHeroes();
            if (r.queued === 0 && r.skipped_in_flight === 0) {
                toast.success(
                    "All packages already have a hero — nothing to do.",
                );
            } else {
                const parts = [`${r.queued} hero job(s) queued`];
                if (r.skipped_in_flight) {
                    parts.push(`${r.skipped_in_flight} already in queue`);
                }
                toast.success(parts.join(" · "));
            }
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to queue heroes.",
            );
        } finally {
            setBulkBusy(null);
        }
    };

    const handleBulkGenerateMissing = async () => {
        if (missingTierCount === 0) {
            toast.info("All packages already at green tier — nothing to backfill.");
            return;
        }
        if (!window.confirm(
            `Generate missing assets for ${missingTierCount} package(s)? `
            + "Estimated cost: ~$" + (missingTierCount * 0.5).toFixed(2)
            + "–" + (missingTierCount * 1.0).toFixed(2)
            + " in LLM credit. Each package takes ~2-4 min and runs in parallel.",
        )) return;
        setBulkBusy("missing");
        try {
            const r = await adminBulkGenerateMissingAssets();
            toast.success(`Queued ${r.queued} package(s) for asset backfill.`);
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk queue failed.");
        } finally {
            setBulkBusy(null);
        }
    };

    const handleBulkRegenerateAll = async () => {
        setBulkBusy("all");
        try {
            const r = await adminBulkRegenerateAllAssets();
            toast.success(`Queued ${r.queued} package(s) for full regeneration.`);
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk queue failed.");
        } finally {
            setBulkBusy(null);
            setConfirmBulkRegen(false);
        }
    };

    const handleBulkRebrand = async () => {
        setBulkBusy("rebrand");
        try {
            // Step 1 — unarchive every legacy package so the refreshed
            // pins surface in the active feed once the worker finishes.
            const ua = await adminBulkUnarchiveLegacy();
            // Step 2 — enqueue v1_full_asset_pipeline w/ force=true on
            // every active package (the unarchive flip in step 1 brought
            // the legacy ones into scope) so every pin re-renders with
            // the current claymation prompt + new mini-logo lockup.
            const rb = await adminBulkRebrandImages();
            const parts = [];
            if (ua.affected) {
                parts.push(`${ua.affected} package(s) unarchived`);
            }
            parts.push(`${rb.queued} image regen(s) queued`);
            if (rb.skipped_in_flight) {
                parts.push(`${rb.skipped_in_flight} already in queue`);
            }
            toast.success(parts.join(" · "));
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk rebrand failed.");
        } finally {
            setBulkBusy(null);
            setConfirmBulkRebrand(false);
        }
    };

    const handleBulkQAPreview = async () => {
        if (bulkQABusy) return;
        setBulkQABusy(true);
        setBulkQAPreview(null);
        setBulkQAModalOpen(true);
        try {
            const r = await adminBulkQAAutoFix({ dryRun: true });
            setBulkQAPreview(r);
        } catch (e) {
            setBulkQAModalOpen(false);
            toast.error(e?.response?.data?.detail || "Bulk preview failed.");
        } finally {
            setBulkQABusy(false);
        }
    };

    const handleBulkQAApply = async () => {
        if (bulkQAApplying) return;
        setBulkQAApplying(true);
        try {
            const r = await adminBulkQAAutoFix({ dryRun: false });
            setBulkQAModalOpen(false);
            setBulkQAPreview(null);
            const parts = [
                `${r.packages_with_fixes} package(s) fixed`,
                `${r.total_fixes} total fix${r.total_fixes === 1 ? "" : "es"}`,
            ];
            if (r.total_images_queued) {
                parts.push(`${r.total_images_queued} image regen(s) queued`);
            }
            toast.success(parts.join(" · "));
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk QA-fix failed.");
        } finally {
            setBulkQAApplying(false);
        }
    };

    const closeBulkQAModal = () => {
        if (bulkQAApplying) return;
        setBulkQAModalOpen(false);
        setBulkQAPreview(null);
    };

    if (selected) {
        return (
            <div className="space-y-4">
                <button
                    type="button"
                    onClick={() => {
                        setSelected(null);
                        setRefreshTick((t) => t + 1);
                    }}
                    className="bl-btn-ghost text-xs gap-1"
                    data-testid="ce-back-btn"
                >
                    <ChevronLeft className="h-3 w-3" />
                    Back to list
                </button>
                <ContentPackageEditor
                    pkg={selected}
                    onUpdate={(p) => setSelected(p)}
                    onClose={() => {
                        setSelected(null);
                        setRefreshTick((t) => t + 1);
                    }}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-content-packages">
            <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="text-bl-muted">V1 Lean Content Engine ·</span>
                <Link
                    to="/admin/weekly-emails"
                    className="bl-btn-ghost text-xs gap-1 border border-bl-line"
                    data-testid="nav-weekly-emails"
                >
                    <Mail className="h-3 w-3" />
                    Weekly Email Packages
                </Link>
                <Link
                    to="/admin/visual-asset-storage"
                    className="bl-btn-ghost text-xs gap-1 border border-bl-line"
                    data-testid="nav-visual-asset-storage"
                >
                    <HardDrive className="h-3 w-3" />
                    Visual Asset Storage
                </Link>
                <Link
                    to="/admin/metricool"
                    className="bl-btn-ghost text-xs gap-1 border border-bl-line"
                    data-testid="nav-metricool-settings"
                >
                    <Send className="h-3 w-3" />
                    Metricool Settings
                </Link>
            </div>
            <ContentEngineSpendTile />
            <ContentEngineHealthWidget />
            <ContentJobsTile />
            <UniversalKeyBalanceTile />
            <ImageStatusTile />
            <OpportunityCards
                onUseTopic={(topic) => {
                    formRef.current?.applyTopic?.(topic);
                }}
            />
            <GeneratePackageForm
                ref={formRef}
                onCreated={(pkg) => {
                    setRefreshTick((t) => t + 1);
                    setSelected(pkg);
                }}
            />

            {/* V1 Lean Content Engine — clean-slate legacy archive tile.
                Surfaces migration state + one-click run + view toggle.    */}
            <div
                className="bl-card p-5 border-l-4 border-l-[var(--bl-teal)]"
                data-testid="ce-legacy-archive-tile"
            >
                <div className="flex items-start gap-3 flex-wrap">
                    <Archive className="h-5 w-5 text-[var(--bl-teal)] shrink-0 mt-1" />
                    <div className="flex-1 min-w-[260px]">
                        <h3 className="font-display font-semibold text-base text-bl mb-1">
                            V1 Lean Content Engine · Clean Slate
                        </h3>
                        <p className="text-xs text-bl-muted mb-2 max-w-xl">
                            Legacy packages are archived and hidden from the
                            active V1 content engine. Published articles and
                            article infographics remain active and eligible
                            for fresh Pinterest + email package generation.
                        </p>
                        {legacyStatus && (
                            <div
                                className="text-xs text-bl flex items-center gap-4 flex-wrap"
                                data-testid="ce-legacy-archive-counters"
                            >
                                <span>
                                    Active:{" "}
                                    <strong className="font-mono">
                                        {legacyStatus.packages?.active ?? 0}
                                    </strong>
                                </span>
                                <span>
                                    Legacy:{" "}
                                    <strong className="font-mono">
                                        {legacyStatus.packages?.archived ?? 0}
                                    </strong>
                                </span>
                                <span>
                                    Articles migrated:{" "}
                                    <strong className="font-mono">
                                        {legacyStatus.articles?.clean_slate_migrated ?? 0}
                                    </strong>
                                    /{legacyStatus.articles?.total ?? 0}
                                </span>
                                {legacyStatus.last_run?.ran_at && (
                                    <span className="text-bl-muted">
                                        Last run:{" "}
                                        {legacyStatus.last_run.ran_at.slice(0, 10)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div
                            className="inline-flex rounded border border-bl overflow-hidden"
                            data-testid="ce-view-mode-toggle"
                        >
                            <button
                                type="button"
                                onClick={() => setViewMode("active")}
                                className={
                                    "text-xs px-3 py-1.5 transition-colors "
                                    + (viewMode === "active"
                                        ? "bg-bl-primary text-bl-surface"
                                        : "bg-bl-surface text-bl hover:bg-bl-bg")
                                }
                                data-testid="ce-view-active-btn"
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("legacy")}
                                className={
                                    "text-xs px-3 py-1.5 transition-colors border-l border-bl "
                                    + (viewMode === "legacy"
                                        ? "bg-bl-primary text-bl-surface"
                                        : "bg-bl-surface text-bl hover:bg-bl-bg")
                                }
                                data-testid="ce-view-legacy-btn"
                            >
                                Legacy archive
                                {legacyStatus?.packages?.archived > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-bl-bg text-bl text-[10px] font-mono">
                                        {legacyStatus.packages.archived}
                                    </span>
                                )}
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => setConfirmLegacyRun(true)}
                            disabled={legacyBusy}
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl"
                            data-testid="ce-legacy-run-btn"
                            title="Idempotently migrate every active package to the legacy archive + reset article email/pinterest eligibility"
                        >
                            {legacyBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3 w-3" />
                            )}
                            Run clean-slate migration
                        </button>
                    </div>
                </div>
                {confirmLegacyRun && (
                    <div
                        className="mt-3 rounded-lg border border-[var(--bl-teal)] bg-[var(--bl-teal-tint)] px-4 py-3 text-sm"
                        data-testid="ce-legacy-confirm"
                    >
                        <div className="flex items-start gap-2.5">
                            <Info className="h-4 w-4 text-[var(--bl-teal)] shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-medium text-bl mb-1">
                                    Run clean-slate migration?
                                </div>
                                <div className="text-xs text-bl-muted mb-3">
                                    Marks every active content package as
                                    legacy-archived (hidden from active
                                    dashboards) and resets article-level
                                    <code className="font-mono"> email_status </code>
                                    +<code className="font-mono"> pinterest_status </code>
                                    so the V1 engine treats every article as
                                    fresh. Published articles + infographics
                                    are preserved. The migration is fully
                                    idempotent — safe to run multiple times.
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleLegacyRun}
                                        disabled={legacyBusy}
                                        className="bl-btn-primary text-xs gap-1.5"
                                        data-testid="ce-legacy-confirm-btn"
                                    >
                                        {legacyBusy ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-3 w-3" />
                                        )}
                                        Yes, run migration
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmLegacyRun(false)}
                                        disabled={legacyBusy}
                                        className="bl-btn-ghost text-xs"
                                        data-testid="ce-legacy-cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bl-card p-5">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <h2 className="font-display font-semibold text-xl text-bl">
                        Content packages
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={handleBulkGenerateMissing}
                            disabled={bulkBusy !== null || missingTierCount === 0}
                            className="bl-btn-primary text-xs gap-1.5"
                            data-testid="ce-bulk-generate-missing"
                            title={
                                missingTierCount === 0
                                    ? "All packages already at green tier"
                                    : `Backfill missing assets on ${missingTierCount} package(s)`
                            }
                        >
                            {bulkBusy === "missing" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Sparkles className="h-3 w-3" />
                            )}
                            Generate missing
                            {missingTierCount > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[var(--bl-teal-tint)] text-bl-primary text-[10px] font-mono">
                                    {missingTierCount}
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmBulkRegen(true)}
                            disabled={bulkBusy !== null || list.length === 0}
                            className="bl-btn-ghost text-xs gap-1.5 border border-[var(--bl-rust)]/40 text-[var(--bl-rust)]"
                            data-testid="ce-bulk-regenerate-all"
                            title="DESTRUCTIVE: wipe + regenerate every secondary asset on every package"
                        >
                            {bulkBusy === "all" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3 w-3" />
                            )}
                            Regenerate all
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmBulkRebrand(true)}
                            disabled={bulkBusy !== null || list.length === 0}
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl-primary/40 text-bl-primary"
                            data-testid="ce-bulk-rebrand-images"
                            title="Re-render every Pinterest pin + infographic with the current claymation prompt and new mini-logo lockup. Unarchives legacy packages so the refreshed pins surface in the active feed."
                        >
                            {bulkBusy === "rebrand" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Brush className="h-3 w-3" />
                            )}
                            Refresh branding
                        </button>
                        <button
                            type="button"
                            onClick={handleBulkGenerateMissingHeroes}
                            disabled={bulkBusy !== null || list.length === 0}
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl-primary/40 text-bl-primary"
                            data-testid="ce-bulk-missing-heroes"
                            title="Find every active package with a missing hero image and enqueue a hero render via Nano Banana. Skips fresh stubs (full pipeline will handle those) and packages with a hero job already in flight. ~$0.10 per hero, runs in background."
                        >
                            {bulkBusy === "missing-heroes" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <ImageIcon className="h-3 w-3" />
                            )}
                            Fill missing heroes
                        </button>
                        <button
                            type="button"
                            onClick={handleBulkQAPreview}
                            disabled={bulkBusy !== null || bulkQABusy || list.length === 0}
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl-primary/40 text-bl-primary"
                            data-testid="ce-bulk-qa-fix"
                            title="Preview + apply QA Auto-Fix across every package with errors"
                        >
                            {bulkQABusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Wrench className="h-3 w-3" />
                            )}
                            QA Auto-Fix all
                        </button>
                        <label className="text-xs text-bl-muted flex items-center gap-1">
                            <Search className="h-3 w-3" />
                            Status:
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="ml-1 bg-bl-bg border border-bl rounded px-2 py-1 text-xs"
                                data-testid="ce-status-filter"
                            >
                                <option value="">all</option>
                                <option value="draft">draft</option>
                                <option value="generated">generated</option>
                                <option value="needs_review">needs review</option>
                                <option value="approved">approved</option>
                                <option value="published">published</option>
                                <option value="archived">archived</option>
                            </select>
                        </label>
                    </div>
                </div>


                {staleArticleCount > 0 && bulkBusy === null && (
                    <div
                        className="mb-4 rounded-lg border border-[var(--bl-amber)] bg-[var(--bl-amber-tint)] px-4 py-3 text-sm flex items-center gap-3"
                        data-testid="ce-library-stale-banner"
                    >
                        <Circle
                            className="h-2 w-2 fill-[var(--bl-amber)] text-[var(--bl-amber)] shrink-0"
                            strokeWidth={0}
                        />
                        <div className="flex-1">
                            <span className="font-medium text-bl">
                                {staleArticleCount} article
                                {staleArticleCount === 1 ? "" : "s"} ·{" "}
                                {stalePlatformTotal} stale platform
                                {stalePlatformTotal === 1 ? "" : "s"}
                            </span>{" "}
                            <span className="text-bl-muted">
                                — social copy was written before the article
                                body was last regenerated. Refresh affected
                                platforms to bring everything back into sync.
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleBulkGenerateMissing}
                            disabled={bulkBusy !== null}
                            className="bl-btn-secondary text-[11px] gap-1 shrink-0"
                            data-testid="ce-library-stale-banner-refresh-btn"
                            title="Re-generate missing/stale assets across affected packages"
                        >
                            <Sparkles className="h-3 w-3" />
                            Refresh
                        </button>
                    </div>
                )}

                {confirmBulkRegen && (
                    <div
                        className="mb-4 rounded-lg border border-[var(--bl-rust)] bg-[var(--bl-rust-tint)] px-4 py-3 text-sm"
                        data-testid="ce-bulk-regen-confirm"
                    >
                        <div className="flex items-start gap-2.5">
                            <AlertTriangle className="h-4 w-4 text-[var(--bl-rust)] shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-medium text-bl mb-1">
                                    Regenerate every secondary asset on {list.length} packages?
                                </div>
                                <div className="text-xs text-bl-muted mb-3">
                                    This is destructive: hero images, social copy, Pinterest pins,
                                    and Instagram slides for every package will be wiped and
                                    re-rolled from scratch. Article bodies are preserved.
                                    {` Estimated cost: ~$${(list.length * 0.5).toFixed(2)}–$${(list.length * 1.0).toFixed(2)} in LLM credit.`}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleBulkRegenerateAll}
                                        disabled={bulkBusy !== null}
                                        className="bl-btn-primary text-xs gap-1.5 bg-[var(--bl-rust)] border-[var(--bl-rust)] hover:bg-[var(--bl-rust)]/90"
                                        data-testid="ce-bulk-regen-confirm-btn"
                                    >
                                        {bulkBusy === "all" ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-3 w-3" />
                                        )}
                                        Yes, regenerate all
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmBulkRegen(false)}
                                        disabled={bulkBusy !== null}
                                        className="bl-btn-ghost text-xs"
                                        data-testid="ce-bulk-regen-cancel"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {confirmBulkRebrand && (
                    <div
                        className="mb-4 rounded-lg border border-bl-primary bg-[var(--bl-teal-tint)] px-4 py-3 text-sm"
                        data-testid="ce-bulk-rebrand-confirm"
                    >
                        <div className="flex items-start gap-2.5">
                            <Brush className="h-4 w-4 text-bl-primary shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-medium text-bl mb-1">
                                    Refresh branding across the library?
                                </div>
                                <div className="text-xs text-bl-muted mb-3">
                                    For every package: <b>unarchives</b> any
                                    {" "}<code>legacy_archived</code> rows so the
                                    refreshed pins resurface in the active feed,
                                    then queues a force re-render of all 9 image
                                    assets (1 infographic + 8 Pinterest pins) with
                                    the current claymation prompt + new mini-logo
                                    lockup. Pin <b>copy</b> (titles, descriptions)
                                    and article bodies are preserved.
                                    {` Estimated cost: ~$${(list.length * 0.12).toFixed(2)}–$${(list.length * 0.20).toFixed(2)} in Universal Key credit. ETA ~6 min per package, runs in background.`}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleBulkRebrand}
                                        disabled={bulkBusy !== null}
                                        className="bl-btn-primary text-xs gap-1.5"
                                        data-testid="ce-bulk-rebrand-confirm-btn"
                                    >
                                        {bulkBusy === "rebrand" ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Brush className="h-3 w-3" />
                                        )}
                                        Yes, refresh branding
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmBulkRebrand(false)}
                                        disabled={bulkBusy !== null}
                                        className="bl-btn-ghost text-xs"
                                        data-testid="ce-bulk-rebrand-cancel"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="py-8 text-center">
                        <Loader2 className="h-5 w-5 animate-spin inline-block text-bl-muted" />
                    </div>
                ) : list.length === 0 ? (
                    <p
                        className="text-sm text-bl-muted text-center py-6"
                        data-testid="ce-empty-state"
                    >
                        {viewMode === "legacy"
                            ? "No legacy packages. The clean-slate migration has not run yet, or every package has been restored."
                            : "Active workflow is empty — a clean V1 slate. Generate your first lean content package above."}
                    </p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-bl-muted border-b border-bl">
                                <th className="py-2">Title</th>
                                <th className="py-2">Status</th>
                                <th className="py-2">Assets</th>
                                <th className="py-2">Created</th>
                                <th className="py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((p) => (
                                <tr
                                    key={p.package_id}
                                    className="border-b border-bl/30 last:border-0 hover:bg-bl-bg/60 cursor-pointer"
                                    onClick={() => openPackage(p.package_id)}
                                    data-testid={`ce-row-${p.package_id}`}
                                >
                                    <td className="py-3 pr-2">
                                        {p.title ||
                                            p.generation_input?.primary_keyword ||
                                            p.package_id}
                                        <div className="text-[10px] text-bl-muted font-mono">
                                            {p.package_id}
                                        </div>
                                    </td>
                                    <td className="py-3">
                                        <span
                                            className={
                                                "text-xs font-medium " +
                                                (STATUS_COLORS[p.status] || "text-bl-muted")
                                            }
                                        >
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="py-3">
                                        <div className="inline-flex items-center gap-2 flex-wrap">
                                            <AssetCompletionDot completion={p.asset_completion} />
                                            {p.freshness?.stale > 0 && (
                                                <span
                                                    className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-[var(--bl-amber-tint)] text-[var(--bl-amber)] border border-[var(--bl-amber)]/60 inline-flex items-center gap-1"
                                                    title={
                                                        `${p.freshness.stale} of 7 social platforms are stale — their copy was written before the article body was last regenerated (`
                                                        + (p.freshness.stale_platforms || []).join(", ") + ")"
                                                    }
                                                    data-testid={`ce-row-stale-${p.package_id}`}
                                                >
                                                    <Circle className="h-1.5 w-1.5 fill-current" strokeWidth={0} />
                                                    {p.freshness.stale} stale
                                                </span>
                                            )}
                                        </div>
                                        <PackageProgressLine pkg={p} />
                                    </td>
                                    <td className="py-3 text-bl-muted text-xs">
                                        {p.created_at?.slice(0, 10)}
                                    </td>
                                    <td className="py-3 text-right">
                                        <div
                                            className="inline-flex items-center gap-1"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {viewMode === "legacy" ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="bl-btn-ghost text-xs gap-1 px-2 border border-[var(--bl-teal)] text-[var(--bl-teal)]"
                                                        data-testid={`ce-row-restore-${p.package_id}`}
                                                        disabled={restoreBusy[p.package_id]}
                                                        onClick={() => handleLegacyRestore(p.package_id)}
                                                        title="Restore this legacy package to the active workflow"
                                                    >
                                                        {restoreBusy[p.package_id] ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <RotateCcw className="h-3 w-3" />
                                                        )}
                                                        Restore
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bl-btn-ghost text-xs"
                                                        data-testid={`ce-open-${p.package_id}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openPackage(p.package_id);
                                                        }}
                                                    >
                                                        Open →
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                            <button
                                                type="button"
                                                className="bl-btn-ghost text-xs gap-1 px-2"
                                                data-testid={`ce-row-generate-${p.package_id}`}
                                                disabled={
                                                    rowBusy[p.package_id] !== undefined
                                                    || p.asset_generation_status === "in_progress"
                                                    || p.asset_completion?.tier === "green"
                                                }
                                                onClick={() => handleRowGenerate(p.package_id)}
                                                title={
                                                    p.asset_completion?.tier === "green"
                                                        ? "Already complete"
                                                        : `Generate ${p.asset_completion?.missing?.length || ""} missing asset(s)`
                                                }
                                            >
                                                {rowBusy[p.package_id] === "missing" ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <Sparkles className="h-3 w-3" />
                                                )}
                                                Generate
                                            </button>
                                            <button
                                                type="button"
                                                className="bl-btn-ghost text-xs gap-1 px-2"
                                                data-testid={`ce-row-regenerate-${p.package_id}`}
                                                disabled={
                                                    rowBusy[p.package_id] !== undefined
                                                    || p.asset_generation_status === "in_progress"
                                                }
                                                onClick={() => handleRowRegenerate(p.package_id)}
                                                title="Wipe + regenerate every secondary asset"
                                            >
                                                {rowBusy[p.package_id] === "all" ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="h-3 w-3" />
                                                )}
                                                Regen
                                            </button>
                                            <button
                                                type="button"
                                                className="bl-btn-ghost text-xs gap-1 px-2"
                                                data-testid={`ce-row-hero-${p.package_id}`}
                                                disabled={
                                                    rowBusy[p.package_id] !== undefined
                                                    || p.hero_regeneration_status === "in_progress"
                                                    || p.asset_completion?.hero_regen_active
                                                }
                                                onClick={() => handleRowRegenerateHero(p.package_id)}
                                                title={
                                                    p.hero_image?.relative_path
                                                        ? "Regenerate the claymation hero image via Nano Banana (~$0.10, ~30 s)."
                                                        : "Generate a claymation hero image via Nano Banana (~$0.10, ~30 s). Article infographic stays unchanged."
                                                }
                                            >
                                                {rowBusy[p.package_id] === "hero"
                                                    || p.hero_regeneration_status === "in_progress"
                                                    || p.asset_completion?.hero_regen_active ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <ImageIcon className="h-3 w-3" />
                                                )}
                                                {p.hero_image?.relative_path ? "Hero" : "Hero +"}
                                            </button>
                                            <button
                                                type="button"
                                                className="bl-btn-ghost text-xs gap-1 px-2 border border-[var(--bl-teal)]/30 text-[var(--bl-teal)]"
                                                data-testid={`ce-row-schedule-month-${p.package_id}`}
                                                disabled={
                                                    rowBusy[p.package_id] !== undefined
                                                    || p.asset_completion?.tier !== "green"
                                                    || p.status === "draft"
                                                }
                                                onClick={() => handleRowScheduleMonth(p.package_id, p.title)}
                                                title={
                                                    p.status === "draft"
                                                        ? "Publish the article first"
                                                        : p.asset_completion?.tier !== "green"
                                                            ? "All 8 pins must be generated first"
                                                            : "Schedule all 8 pins evenly over 30 days (first pin tomorrow 14:00 UTC, ~4 days between)"
                                                }
                                            >
                                                {rowBusy[p.package_id] === "schedule" ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <CalendarDays className="h-3 w-3" />
                                                )}
                                                Schedule month
                                            </button>
                                            <button
                                                type="button"
                                                className="bl-btn-ghost text-xs"
                                                data-testid={`ce-open-${p.package_id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openPackage(p.package_id);
                                                }}
                                            >
                                                Open →
                                            </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <BulkQAFixModal
                open={bulkQAModalOpen}
                preview={bulkQAPreview}
                loading={bulkQABusy && !bulkQAPreview}
                applying={bulkQAApplying}
                onClose={closeBulkQAModal}
                onApply={handleBulkQAApply}
            />
        </div>
    );
}

function BulkQAFixModal({ open, preview, loading, applying, onClose, onApply }) {
    if (!open) return null;
    const scanned = preview?.scanned ?? 0;
    const pkgFixed = preview?.packages_with_fixes ?? 0;
    const totalFixes = preview?.total_fixes ?? 0;
    const totalDiffs = preview?.total_diffs ?? 0;
    const totalImgs = preview?.total_images_queued ?? 0;
    const errBefore = preview?.errors_before ?? 0;
    const errAfter = preview?.errors_after ?? 0;
    const perPkg = preview?.per_package ?? [];
    const noWork = !loading && pkgFixed === 0 && totalImgs === 0;
    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            data-testid="bulk-qa-fix-modal"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-bl-bg bl-card max-w-3xl w-full max-h-[88vh] flex flex-col shadow-2xl">
                <div className="flex items-start justify-between gap-4 p-5 border-b border-bl/30">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Wrench className="h-5 w-5 text-bl-primary" />
                            <h2 className="font-display font-semibold text-xl text-bl tracking-tight">
                                Bulk QA Auto-Fix · Preview
                            </h2>
                        </div>
                        <p className="text-xs text-bl-muted mt-1 max-w-2xl">
                            Scans every non-archived package, computes deterministic fixes, and surfaces the impact before any commit.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={applying}
                        className="text-bl-muted hover:text-bl"
                        data-testid="bulk-qa-fix-modal-close"
                        aria-label="Close preview"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-bl-muted">
                            <Loader2 className="h-4 w-4 animate-spin" /> Scanning library…
                        </div>
                    ) : noWork ? (
                        <div
                            className="bl-card p-4 flex items-start gap-3"
                            data-testid="bulk-qa-fix-modal-empty"
                        >
                            <Check className="h-5 w-5 text-[var(--bl-green)] mt-0.5" />
                            <div>
                                <div className="font-semibold text-bl">Nothing to fix.</div>
                                <p className="text-xs text-bl-muted mt-1">
                                    Scanned {scanned} package(s). No structural QA findings or failed images need attention.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs"
                                data-testid="bulk-qa-fix-modal-summary"
                            >
                                <Tile label="Scanned" value={scanned} />
                                <Tile label="Packages" value={pkgFixed} accent="primary" />
                                <Tile label="Fixes" value={totalFixes} accent="primary" />
                                <Tile label="Image regens" value={totalImgs} />
                                <Tile
                                    label="QA errors"
                                    value={`${errBefore} → ${errAfter}`}
                                    accent={errAfter === 0 ? "green" : "amber"}
                                />
                            </div>
                            <section data-testid="bulk-qa-fix-modal-list">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-bl-muted mb-2">
                                    Per package
                                </h3>
                                <div className="overflow-x-auto border border-bl/30 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-bl-bg/60 text-bl-muted">
                                            <tr>
                                                <th className="text-left px-3 py-2">Title</th>
                                                <th className="text-right px-3 py-2">Fixes</th>
                                                <th className="text-right px-3 py-2">Diffs</th>
                                                <th className="text-right px-3 py-2">Errors</th>
                                                <th className="text-right px-3 py-2">Images</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {perPkg.map((p) => (
                                                <tr
                                                    key={p.package_id}
                                                    className="border-t border-bl/20"
                                                    data-testid={`bulk-qa-fix-row-${p.package_id}`}
                                                >
                                                    <td className="px-3 py-2 truncate max-w-xs text-bl" title={p.title}>
                                                        {p.title}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono">{p.fixes_count}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{p.diffs_count}</td>
                                                    <td className="px-3 py-2 text-right font-mono">
                                                        <span className="text-[var(--bl-rust)]">{p.errors_before}</span>
                                                        <span className="text-bl-muted px-1">→</span>
                                                        <span className={p.errors_after === 0 ? "text-[var(--bl-green)]" : "text-[var(--bl-amber)]"}>
                                                            {p.errors_after}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono">{p.images_queued}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </>
                    )}
                </div>
                <div className="border-t border-bl/30 p-4 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-bl-muted">
                        Deterministic fixes — no LLM call. Image regens queue separately and may take a few minutes.
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={applying}
                            className="bl-btn-ghost text-xs"
                            data-testid="bulk-qa-fix-modal-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onApply}
                            disabled={loading || applying || noWork}
                            className="bl-btn-primary text-xs gap-1.5"
                            data-testid="bulk-qa-fix-modal-apply"
                        >
                            {applying ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Check className="h-3.5 w-3.5" />
                            )}
                            {applying ? "Applying…" : `Apply to ${pkgFixed} package(s)`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Tile({ label, value, accent }) {
    const accentClass =
        accent === "primary" ? "text-bl-primary"
        : accent === "green" ? "text-[var(--bl-green)]"
        : accent === "amber" ? "text-[var(--bl-amber)]"
        : "text-bl";
    return (
        <div className="bl-card p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-bl-muted">{label}</div>
            <div className={`font-display font-semibold text-lg ${accentClass}`}>{value}</div>
        </div>
    );
}
